import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Github, Linkedin, Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState({ submitted: false, success: false, message: '', isLoading: false });

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus({ isLoading: true, submitted: false, success: false, message: '' });

    const form = e.target;
    const body = new FormData(form);

    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(body).toString(),
    })
      .then(() => {
        setFormStatus({
          submitted: true,
          success: true,
          message: "Got it! I'll get back to you soon.",
          isLoading: false,
        });
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus({ submitted: false, success: false, message: '', isLoading: false }), 5000);
      })
      .catch((error) => {
        setFormStatus({
          submitted: true,
          success: false,
          message: 'Something went wrong. Please try again.',
          isLoading: false,
        });
        setTimeout(() => setFormStatus({ submitted: false, success: false, message: '', isLoading: false }), 5000);
        console.error('Error:', error);
      });
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: 'tushar.anand.bpmce@gmail.com', href: 'mailto:tushar.anand.bpmce@gmail.com', accent: 'bg-sun' },
    { icon: Phone, label: 'Phone', value: '+91-9060177959', href: 'tel:+919060177959', accent: 'bg-mint' },
    { icon: MapPin, label: 'Location', value: 'Bhagalpur, India', accent: 'bg-sky' },
  ];

  return (
    <section id="contact" className="py-20 md:py-28 bg-paper dark:bg-noir">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <p className="font-display font-bold text-ink/60 dark:text-cream/60 mb-2">Got an idea?</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold text-ink dark:text-cream">
            Let's Build{' '}
            <span className="inline-block -rotate-1 bg-bubble px-3 brutal-border brutal-shadow-sm">
              Something
            </span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-3 rounded-2xl bg-cream dark:bg-card p-6 md:p-8 brutal-border brutal-shadow-lg"
          >
            <h3 className="font-display text-2xl font-extrabold text-ink dark:text-cream mb-1">Send a message</h3>
            <p className="text-ink/70 dark:text-cream/70 mb-6">I usually reply within 24 hours.</p>

            {formStatus.submitted && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex items-center gap-3 p-4 mb-6 rounded-xl brutal-border-thin font-semibold ${
                  formStatus.success ? 'bg-mint text-ink' : 'bg-flame text-cream'
                }`}
              >
                {formStatus.success ? <CheckCircle2 className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                <span>{formStatus.message}</span>
              </motion.div>
            )}

            <form name="contact" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={handleSubmit} className="space-y-5">
              <input type="hidden" name="form-name" value="contact" />
              <div hidden>
                <label>
                  Don't fill this out if you're human:
                  <input name="bot-field" />
                </label>
              </div>

              <div>
                <label className="block font-bold text-ink dark:text-cream mb-2" htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl bg-paper dark:bg-noir text-ink dark:text-cream placeholder-ink/40 dark:placeholder-cream/40 brutal-border-thin focus:outline-none focus:brutal-shadow-sm transition-shadow"
                  placeholder="Your full name"
                  required
                />
              </div>

              <div>
                <label className="block font-bold text-ink dark:text-cream mb-2" htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl bg-paper dark:bg-noir text-ink dark:text-cream placeholder-ink/40 dark:placeholder-cream/40 brutal-border-thin focus:outline-none focus:brutal-shadow-sm transition-shadow"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <label className="block font-bold text-ink dark:text-cream mb-2" htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl bg-paper dark:bg-noir text-ink dark:text-cream placeholder-ink/40 dark:placeholder-cream/40 brutal-border-thin focus:outline-none focus:brutal-shadow-sm transition-shadow resize-none"
                  placeholder="Tell me about your project..."
                  required
                />
              </div>

              <button
                type="submit"
                disabled={formStatus.isLoading}
                className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-full bg-sun text-ink font-display font-bold brutal-border brutal-hover disabled:opacity-60"
              >
                {formStatus.isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="rounded-2xl bg-cream dark:bg-card p-6 md:p-8 brutal-border brutal-shadow">
              <h3 className="font-display text-xl font-extrabold text-ink dark:text-cream mb-6">Contact Info</h3>
              <div className="space-y-5">
                {contactInfo.map(({ icon: Icon, label, value, href, accent }) => (
                  <div key={label} className="flex items-start gap-4">
                    <div className={`flex-shrink-0 w-11 h-11 rounded-xl ${accent} brutal-border-thin flex items-center justify-center`}>
                      <Icon className="w-5 h-5 text-ink" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-bold text-ink dark:text-cream text-sm mb-0.5">{label}</h4>
                      {href ? (
                        <a href={href} className="text-ink/70 dark:text-cream/70 hover:text-ink dark:hover:text-cream text-sm break-all transition-colors">
                          {value}
                        </a>
                      ) : (
                        <p className="text-ink/70 dark:text-cream/70 text-sm">{value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-cream dark:bg-card p-6 md:p-8 brutal-border brutal-shadow">
              <h4 className="font-display text-xl font-extrabold text-ink dark:text-cream mb-5">Find Me Online</h4>
              <div className="grid grid-cols-2 gap-4">
                <a
                  href="https://github.com/theandroguy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-paper dark:bg-noir brutal-border-thin brutal-hover"
                >
                  <Github className="w-7 h-7 text-ink dark:text-cream" />
                  <span className="text-sm font-bold text-ink dark:text-cream">GitHub</span>
                </a>
                <a
                  href="https://linkedin.com/in/tusharanand2000"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-paper dark:bg-noir brutal-border-thin brutal-hover"
                >
                  <Linkedin className="w-7 h-7 text-ink dark:text-cream" />
                  <span className="text-sm font-bold text-ink dark:text-cream">LinkedIn</span>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
